import axios from 'axios';
import { getAuthHeader } from '../auth/identity';
import type { ChatbotScope } from '../context/ChatbotScopeContext';

// <purpose>Shared HTTP client for the smarttix.chatbot App Service (per-customer Bicep deployment).</purpose>
// <dependencies>getAuthHeader (auth/identity.ts) — Authorization: Bearer for the admin path.</dependencies>
//
// Base URL resolved at runtime from /api/config. Falls back to build-time env var
// for local dev. Call initChatbotClient() from App.tsx after fetching config.

const CHATBOT_LOCAL_FALLBACK = 'http://localhost:5050/api';

const chatbotHttp = axios.create({ baseURL: process.env.REACT_APP_CHATBOT_API_URL || CHATBOT_LOCAL_FALLBACK });
chatbotHttp.interceptors.request.use(async (config) => {
  const auth = await getAuthHeader();
  if (auth) config.headers[auth.name] = auth.value;
  return config;
});

// Why: called by App.tsx after /api/config resolves. Updates the axios instance's base URL
// so all subsequent chatbot calls go to the right endpoint.
export const initChatbotClient = (baseUrl: string) => {
  if (baseUrl) chatbotHttp.defaults.baseURL = baseUrl;
};

// Why: SSE streaming (ReadableStream) requires fetch(), not axios. This helper
// provides the same auto-auth headers for streaming calls in ChatbotTestSeite.
// Returns the current base URL so callers can construct streaming URLs.
export const getChatbotHeaders = async (): Promise<Record<string, string>> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const auth = await getAuthHeader();
  if (auth) headers[auth.name] = auth.value;
  return headers;
};

export const getChatbotBaseUrl = (): string => chatbotHttp.defaults.baseURL as string;

// ─── Typed API functions ────────────────────────────────

export const createConversation = async (): Promise<string> => {
  const res = await chatbotHttp.post('/chat/conversations', {});
  return res.data.conversationId;
};

/**
 * The analytics shape the Statistikseite card consumes. The live GET /api/chat/analytics
 * endpoint (smarttix.chatbot GetAnalyticsAsync) returns exactly this shape. A `null` numeric
 * means "not yet available" — the card renders "noch nicht erfasst"/"—", never a fake value.
 */
export interface ChatbotAnalytics {
  scope: ChatbotScope;
  containmentRate: number | null;      // 0..100 — hero. proxy: convos with none of {eskalation,kontaktformular,akute_gefahr,nicht_zustaendig}
  containmentTrendPp: number | null;   // percentage-points vs prior period; null when disjoint/unavailable
  topics: { key: string; label: string; sharePct: number }[];  // ranked; card shows top 3; label = raw K-code (FE resolves)
  gespraeche: number;                  // conversation count (all scoped, incl. KU)
  gespraecheTrendPct: number | null;   // % vs prior period
  hilfreichRate: number | null;        // 0..100 positive/(pos+neg) over chatbot_messages.feedback
  hilfreichN: number | null;           // sample size
  unclassifiedRate: number | null;     // 0..100 unclassified/total ("Nicht verstanden")
  // weekly (KW), split by axis. `total` here is itself unclassified-excluded (the backend's own
  // SQL filters 'nicht verstanden' out before summing), so byPrimaryAxis sums to it by
  // construction — this is not an instance of the ticket-classification "performance surface drops
  // the unclassified bucket" rule (that rule governs RATE computations over categoryKeys/
  // performanceCategoryKeys) nor of the "volume stack reconciles with unclassified bucket included"
  // rule (categoryKeys' reconciliation set). Both rules describe the ticket-classification data
  // those accessors serve; this is a different endpoint's data (chatbot topic breakdown) and
  // inherits neither by proximity.
  konversationsaufkommen: {
    binLabel: string; binStart: string; total: number; byPrimaryAxis: Record<string, number>;
  }[];
}

// Phase 4B expand-window boundary coalesce (spec §6, task F1): the FE speaks ONLY
// unclassifiedRate/byPrimaryAxis internally; kuRate/byCategory are tolerated ONLY here, at the
// parse boundary, deleted at the contraction (see smarttix-core.md § Contraction discipline). No
// component below this line may mention the legacy names.
const normalizeChatbotAnalytics = (raw: any): ChatbotAnalytics => ({
  ...raw,
  unclassifiedRate: raw.unclassifiedRate ?? raw.kuRate,
  konversationsaufkommen: raw.konversationsaufkommen?.map((bin: any) => ({
    ...bin,
    byPrimaryAxis: bin.byPrimaryAxis ?? bin.byCategory,
  })),
});

// Why: returns null instead of throwing — the chatbot may not be deployed for this customer,
// or /analytics may 4xx/5xx; the Statistikseite treats null as "section absent" (never a fake number).
export const getChatbotAnalytics = async (
  from: string,
  to: string,
  scope: ChatbotScope,
): Promise<ChatbotAnalytics | null> => {
  try {
    const res = await chatbotHttp.get('/chat/analytics', { params: { from, to, scope } });
    return normalizeChatbotAnalytics(res.data);
  } catch {
    return null;
  }
};
